import { keepPreviousData, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  BarChart3,
  CheckCircle2,
  ChevronRight,
  ExternalLink,
  FolderOpen,
  Info,
  XCircle,
} from 'lucide-react';
import { useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Area, Bar, BarChart, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { api } from '../lib/api';
import { DAY_NAMES, formatBytes, formatDateTime, formatNumber, formatPercent, relativeTime } from '../lib/format';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { SITES_KEY } from '../lib/sites';
import {
  DEFAULT_RANGE,
  RANGES,
  TABS,
  bucketLabel,
  changeOf,
  compactNumber,
  countryName,
  dayLabel,
  formatSpan,
  languageName,
  pageAddress,
  pageLink,
  percentOf,
  type AnalyticsSite,
  type AnalyticsTab,
  type BotKind,
  type CheckLevel,
  type LiveReport,
  type RangeKey,
  type RequestsReport,
  type SiteReport,
  type TrafficReport,
} from '../lib/site-analytics';
import {
  Badge,
  Button,
  ButtonLink,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  LoadingBlock,
  Metric,
  PageHeader,
  StatusDot,
  Table,
  Tabs,
  Td,
  Th,
  Tr,
} from '../components/ui';
// The design tokens themselves, as the Tailwind preset reads them: the admin loads no token stylesheet.
import tokens from '../../../../packages/design-tokens/design-tokens.json';

/**
 * A site's Analytics (operator, 2026-11-04: "add analytics button to every site
 * to know everything you can show about the website"), opened from the Sites list
 * and from the site's own page, for whoever looks after the site. Three tabs, one
 * period above them all: Traffic (people: visitors, visits, pages, sources,
 * countries, devices, languages, hours), Requests (everything the address
 * answered, robots too: data, speed, what was not there, robots, files) and Site
 * check (the pages read for broken links and what they lack; what the site is
 * made of, loads and links to; its changes).
 *
 * The two series — visitors and page views — are Sentinel's crimson and plasma
 * violet, validated for colour-blind separation and 3:1 on both themes' cards
 * (crimson one step lighter on the dark card). Every other figure is one series:
 * crimson alone, a wash behind the rows of a list.
 */

const SERIES_STYLE = {
  '--sa-visitors-light': tokens.color.primary['500'],
  '--sa-visitors-dark': tokens.color.primary['400'],
  '--sa-views': tokens.color.ai.plasma,
} as CSSProperties;
const VISITORS = 'var(--sa-visitors)';
const VIEWS = 'var(--sa-views)';

const ROWS_SHOWN = 8;

function useRange(): [RangeKey, AnalyticsTab, (next: { range?: RangeKey; tab?: AnalyticsTab }) => void] {
  const [params, setParams] = useSearchParams();
  const range = (RANGES as readonly string[]).includes(params.get('range') ?? '') ? (params.get('range') as RangeKey) : DEFAULT_RANGE;
  const tab = (TABS as readonly string[]).includes(params.get('tab') ?? '') ? (params.get('tab') as AnalyticsTab) : 'traffic';
  const set = (next: { range?: RangeKey; tab?: AnalyticsTab }) => {
    const out = new URLSearchParams(params);
    if (next.range) out.set('range', next.range);
    if (next.tab) out.set('tab', next.tab);
    setParams(out, { replace: true });
  };
  return [range, tab, set];
}

export function SiteAnalyticsPage() {
  const { id = '' } = useParams();
  const { t, localized } = useI18n();
  const [range, tab, set] = useRange();
  const live = useQuery({
    queryKey: [...SITES_KEY, 'analytics', id, 'live'],
    queryFn: () => api.get<LiveReport>(`/sites/${id}/analytics/live`),
    refetchInterval: 15_000,
  });
  const site = live.data?.site;

  if (live.isLoading) return <LoadingBlock />;
  if (live.error || !site) return <ErrorState error={live.error ?? new Error(t('common.error'))} onRetry={() => void live.refetch()} />;

  return (
    <div style={SERIES_STYLE} className="[--sa-visitors:var(--sa-visitors-light)] dark:[--sa-visitors:var(--sa-visitors-dark)]">
      <nav aria-label={t('sites.where')} className="mb-3 flex flex-wrap items-center gap-1 text-body-sm text-muted">
        <Link to="/sites" className="hover:text-[var(--text-primary)] hover:underline">
          {t('sites.title')}
        </Link>
        <ChevronRight className="h-3.5 w-3.5 flip-rtl text-faint" aria-hidden />
        <Link to={`/sites/${id}`} className="max-w-[14rem] truncate hover:text-[var(--text-primary)] hover:underline">
          {site.name}
        </Link>
        <ChevronRight className="h-3.5 w-3.5 flip-rtl text-faint" aria-hidden />
        <span className="text-[var(--text-secondary)]">{t('analytics.title')}</span>
      </nav>

      <PageHeader
        title={
          <span className="inline-flex items-center gap-2">
            <BarChart3 className="h-6 w-6 shrink-0 text-[var(--text-tertiary)]" aria-hidden />
            <span className="break-all">{t('analytics.pageTitle', { name: site.name })}</span>
          </span>
        }
        subtitle={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <LiveBadge live={live.data!} />
            <a
              href={site.address}
              target="_blank"
              rel="noreferrer"
              dir="ltr"
              className="inline-flex items-center gap-1 break-all text-primary-600 hover:underline dark:text-primary-400"
            >
              {site.address}
              <ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden />
            </a>
            <span className="text-faint">{t('sites.lookedAfterBy', { name: localized(site.owner) })}</span>
          </span>
        }
        action={
          <>
            <ButtonLink to={`/sites/${id}`} icon={<FolderOpen className="h-4 w-4" aria-hidden />}>
              {t('analytics.siteFiles')}
            </ButtonLink>
            <a href={site.address} target="_blank" rel="noreferrer">
              <Button icon={<ExternalLink className="h-4 w-4" aria-hidden />} tabIndex={-1}>
                {t('sites.open')}
              </Button>
            </a>
          </>
        }
      />

      {!site.isLive && (
        <Card className="mb-4 border-warning/40">
          <p className="text-body-sm text-warning-fg dark:text-warning-dark">{t('analytics.offline')}</p>
        </Card>
      )}

      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0 grow">
          <Tabs
            tabs={TABS.map((key) => ({ id: key, label: t(`analytics.tab.${key}` as TranslationKey) }))}
            active={tab}
            onChange={(next) => set({ tab: next as AnalyticsTab })}
          />
        </div>
        {tab !== 'site' && <RangePicker value={range} onChange={(next) => set({ range: next })} />}
      </div>

      {tab === 'traffic' && <TrafficTab id={id} range={range} site={site} live={live.data!} />}
      {tab === 'requests' && <RequestsTab id={id} range={range} />}
      {tab === 'site' && <SiteTab id={id} site={site} />}

      <p className="mt-6 max-w-3xl text-caption text-faint">{t('analytics.privacy')}</p>
    </div>
  );
}

/** "3 on the site now", breathing; else when the last visit was. */
function LiveBadge({ live }: { live: LiveReport }) {
  const { t, locale } = useI18n();
  if (live.visitors > 0) {
    return (
      <span className="inline-flex items-center gap-1.5 font-medium text-success-fg dark:text-success" title={t('analytics.liveHint', { n: live.minutes })}>
        <StatusDot tone="online" pulse />
        {t('analytics.liveNow', { n: formatNumber(live.visitors, locale) })}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-muted" title={t('analytics.liveHint', { n: live.minutes })}>
      <StatusDot tone="neutral" />
      {live.lastVisit ? t('analytics.lastVisit', { when: relativeTime(live.lastVisit.at, locale) }) : t('analytics.noVisitsYet')}
    </span>
  );
}

function RangePicker({ value, onChange }: { value: RangeKey; onChange: (range: RangeKey) => void }) {
  const { t } = useI18n();
  return (
    <div role="group" aria-label={t('analytics.rangeLabel')} className="inline-flex flex-wrap rounded-md border hairline bg-[var(--surface-raised)] p-0.5">
      {RANGES.map((key) => (
        <button
          key={key}
          type="button"
          aria-pressed={value === key}
          onClick={() => onChange(key)}
          className={clsx(
            'rounded-sm px-3 py-1.5 text-body-sm font-medium transition-colors duration-fast',
            value === key ? 'bg-primary text-white' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
          )}
        >
          {t(`analytics.range.${key}` as TranslationKey)}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Traffic
// ---------------------------------------------------------------------------

function TrafficTab({ id, range, site, live }: { id: string; range: RangeKey; site: AnalyticsSite; live: LiveReport }) {
  const { t, locale } = useI18n();
  const report = useQuery({
    queryKey: [...SITES_KEY, 'analytics', id, 'traffic', range],
    queryFn: () => api.get<TrafficReport>(`/sites/${id}/analytics`, { range }),
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });
  const data = report.data;
  if (report.isLoading) return <LoadingBlock />;
  if (report.error || !data) return <ErrorState error={report.error ?? new Error(t('common.error'))} onRetry={() => void report.refetch()} />;

  const { totals, previous } = data;
  const vs = t(`analytics.vsPrevious.${range}` as TranslationKey);
  const rate = (bounces: number, visits: number) => (visits ? (bounces / visits) * 100 : 0);
  const average = (seconds: number, visits: number) => (visits ? seconds / visits : 0);

  return (
    <div className={clsx('space-y-4 transition-opacity', report.isPlaceholderData && 'opacity-60')} aria-busy={report.isFetching}>
      <CountingNote since={data.countingSince} />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <Metric
          label={t('analytics.visitors')}
          value={formatNumber(totals.visitors, locale)}
          hint={<Delta now={totals.visitors} before={previous.visitors} vs={vs} />}
        />
        <Metric label={t('analytics.visits')} value={formatNumber(totals.visits, locale)} hint={<Delta now={totals.visits} before={previous.visits} vs={vs} />} />
        <Metric
          label={t('analytics.pageViews')}
          value={formatNumber(totals.pageViews, locale)}
          hint={<Delta now={totals.pageViews} before={previous.pageViews} vs={vs} />}
        />
        <Metric
          label={t('analytics.viewsPerVisit')}
          value={totals.visits ? new Intl.NumberFormat(locale === 'ar' ? 'ar-EG-u-nu-latn' : 'en-GB', { maximumFractionDigits: 1 }).format(totals.pageViews / totals.visits) : '—'}
          hint={<Delta now={totals.visits ? totals.pageViews / totals.visits : 0} before={previous.visits ? previous.pageViews / previous.visits : null} vs={vs} />}
        />
        <Metric
          label={t('analytics.bounceRate')}
          value={totals.visits ? formatPercent(rate(totals.bounces, totals.visits), locale) : '—'}
          hint={
            <Delta
              now={rate(totals.bounces, totals.visits)}
              before={previous.visits && totals.visits ? rate(previous.bounces, previous.visits) : null}
              vs={vs}
              upIsGood={false}
              points
              title={t('analytics.bounceHint')}
            />
          }
        />
        <Metric
          label={t('analytics.visitDuration')}
          value={totals.visits ? formatSpan(average(totals.seconds, totals.visits), locale) : '—'}
          hint={
            <Delta
              now={average(totals.seconds, totals.visits)}
              before={previous.visits ? average(previous.seconds, previous.visits) : null}
              vs={vs}
              title={t('analytics.visitDurationHint')}
            />
          }
        />
      </div>

      <Card>
        <CardHeader title={t('analytics.chartTitle')} subtitle={t('analytics.chartSubtitle', { zone: data.range.timeZone })} />
        <TrafficChart report={data} />
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <PagesCard report={data} site={site} />
        <SourcesCard report={data} />
        <ShareCard
          title={t('analytics.countries')}
          rows={data.countries}
          name={(code) => (code ? countryName(code, locale) : t('analytics.unknown'))}
          footer={t('analytics.geoCredit')}
        />
        <DevicesCard report={data} />
        <ShareCard
          title={t('analytics.languages')}
          subtitle={t('analytics.languagesHint')}
          rows={data.languages}
          name={(code) => (code ? languageName(code, locale) : t('analytics.unknown'))}
        />
        <LiveCard live={live} site={site} />
      </div>

      <Card>
        <CardHeader title={t('analytics.hours')} subtitle={t('analytics.hoursHint', { zone: data.range.timeZone })} />
        {range === '24h' ? <p className="py-6 text-center text-body-sm text-muted">{t('analytics.hoursShortRange')}</p> : <HoursGrid report={data} />}
      </Card>
    </div>
  );
}

/** Since when this site's visits are counted — analytics began on 2026-11-04 — or that none is yet. */
function CountingNote({ since }: { since: string | null }) {
  const { t, locale } = useI18n();
  if (since && Date.now() - Date.parse(since) > 400 * 86_400_000) return null;
  return (
    <p className="flex items-start gap-2 rounded-md border hairline bg-[var(--surface-sunken)] px-3 py-2 text-body-sm text-[var(--text-secondary)]">
      <Info className="mt-0.5 h-4 w-4 shrink-0 text-[var(--text-tertiary)]" aria-hidden />
      {since ? t('analytics.countingSince', { date: formatDateTime(since, locale) }) : t('analytics.notCountingYet')}
    </p>
  );
}

/**
 * The change from the period before: an arrow and a signed figure, coloured by
 * whether that way is good — never colour alone. Bounce rate moves in points.
 * `before` null: the period before had no visits to compare with.
 */
function Delta({
  now,
  before,
  vs,
  upIsGood = true,
  points = false,
  title,
}: {
  now: number;
  before: number | null;
  vs: string;
  upIsGood?: boolean;
  points?: boolean;
  title?: string;
}) {
  const { t, locale } = useI18n();
  const tag = locale === 'ar' ? 'ar-EG-u-nu-latn' : 'en-GB';
  const change = before === null ? null : points ? Math.round((now - before) * 10) / 10 : changeOf(now, before);
  if (change === null) return <span title={title}>{t('analytics.nothingBefore')}</span>;
  if (change === 0) return <span title={title}>{t('analytics.noChange')} · {vs}</span>;
  const good = change > 0 === upIsGood;
  const Arrow = change > 0 ? ArrowUpRight : ArrowDownRight;
  const amount = new Intl.NumberFormat(tag, { maximumFractionDigits: 1, signDisplay: 'always' }).format(change);
  return (
    <span className="inline-flex flex-wrap items-center gap-x-1" title={title}>
      <span className={clsx('inline-flex items-center gap-0.5 font-medium', good ? 'text-success-fg dark:text-success' : 'text-error-fg dark:text-error-dark')}>
        <Arrow className="h-3.5 w-3.5 flip-rtl" aria-hidden />
        <bdi>{points ? t('analytics.pointsChange', { n: amount }) : `${amount}%`}</bdi>
      </span>
      <span>{vs}</span>
    </span>
  );
}

function TrafficChart({ report }: { report: TrafficReport }) {
  const { t, locale } = useI18n();
  const rows = report.series;
  if (!rows.some((r) => r.pageViews > 0)) return <p className="py-12 text-center text-body-sm text-muted">{t('analytics.chartEmpty')}</p>;
  const kind = report.range.bucket;
  return (
    <div>
      <div className="h-64" dir="ltr">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
            <CartesianGrid stroke="var(--line)" vertical={false} />
            <XAxis
              dataKey="bucket"
              tickFormatter={(value: string) => bucketLabel(value, kind, locale)}
              tick={{ fill: 'var(--text-tertiary)', fontSize: 11 }}
              axisLine={{ stroke: 'var(--line)' }}
              tickLine={false}
              minTickGap={18}
            />
            <YAxis
              width={44}
              allowDecimals={false}
              tickFormatter={(value: number) => compactNumber(value, locale)}
              tick={{ fill: 'var(--text-tertiary)', fontSize: 11 }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              cursor={{ stroke: 'var(--text-tertiary)', strokeWidth: 1 }}
              content={({ active, payload }) => {
                const row = active ? (payload?.[0]?.payload as TrafficReport['series'][number] | undefined) : undefined;
                if (!row) return null;
                return (
                  <ChartTip title={bucketLabel(row.bucket, kind, locale, true)}>
                    <TipLine color={VISITORS} value={formatNumber(row.visitors, locale)} label={t('analytics.visitors')} />
                    <TipLine color={VIEWS} value={formatNumber(row.pageViews, locale)} label={t('analytics.pageViews')} line />
                  </ChartTip>
                );
              }}
            />
            <Area
              type="monotone"
              dataKey="visitors"
              stroke={VISITORS}
              strokeWidth={2}
              fill={VISITORS}
              fillOpacity={0.1}
              dot={false}
              activeDot={{ r: 4, fill: VISITORS, stroke: 'var(--surface-card)', strokeWidth: 2 }}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="pageViews"
              stroke={VIEWS}
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              dot={false}
              activeDot={{ r: 4, fill: VIEWS, stroke: 'var(--surface-card)', strokeWidth: 2 }}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-4 text-caption text-[var(--text-secondary)]">
        <span className="inline-flex items-center gap-2">
          <span className="inline-block h-3 w-4 rounded-xs border-t-2" style={{ borderColor: VISITORS, background: `color-mix(in srgb, ${VISITORS} 14%, transparent)` }} aria-hidden />
          {t('analytics.visitors')}
        </span>
        <span className="inline-flex items-center gap-2">
          <span className="inline-block h-0.5 w-4 rounded-pill" style={{ background: VIEWS }} aria-hidden />
          {t('analytics.pageViews')}
        </span>
      </div>
    </div>
  );
}

function ChartTip({ title, children }: { title: string; children: ReactNode }) {
  const { locale } = useI18n();
  return (
    <div className="rounded-md border hairline bg-[var(--surface-raised)] px-3 py-2 text-body-sm shadow-lg" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
      <p className="mb-1 text-caption text-muted">{title}</p>
      {children}
    </div>
  );
}

function TipLine({ color, value, label, line = false }: { color: string; value: string; label: string; line?: boolean }) {
  return (
    <p className="flex items-center gap-2">
      <span className={clsx('inline-block w-3 rounded-pill', line ? 'h-0.5' : 'h-2')} style={{ background: color }} aria-hidden />
      <strong className="tabular text-[var(--text-primary)]">{value}</strong>
      <span className="text-muted">{label}</span>
    </p>
  );
}

/** A card whose list can be read a few ways — top pages, entry pages — switched by small tabs in its corner. */
function SwitchCard({
  title,
  subtitle,
  views,
  footer,
}: {
  title: string;
  subtitle?: string;
  views: Array<{ key: string; label: string; body: ReactNode }>;
  footer?: ReactNode;
}) {
  const [active, setActive] = useState(views[0].key);
  const current = views.find((v) => v.key === active) ?? views[0];
  return (
    <Card className="min-w-0">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-h3 text-[var(--text-primary)]">{title}</h3>
          {subtitle && <p className="mt-0.5 text-body-sm text-muted">{subtitle}</p>}
        </div>
        {views.length > 1 && (
          <div role="tablist" className="flex flex-wrap gap-1">
            {views.map((view) => (
              <button
                key={view.key}
                type="button"
                role="tab"
                aria-selected={view.key === current.key}
                onClick={() => setActive(view.key)}
                className={clsx(
                  'rounded-sm px-2 py-1 text-caption font-medium transition-colors duration-fast',
                  view.key === current.key
                    ? 'bg-primary/10 text-primary-600 dark:bg-primary-400/15 dark:text-primary-400'
                    : 'text-[var(--text-tertiary)] hover:text-[var(--text-primary)]',
                )}
              >
                {view.label}
              </button>
            ))}
          </div>
        )}
      </div>
      {current.body}
      {footer && <p className="mt-3 text-caption text-faint">{footer}</p>}
    </Card>
  );
}

interface BarRow {
  key: string;
  label: ReactNode;
  /** What the bar measures, and its share of the whole. */
  value: number;
  /** The figures beside it, in order of the columns. */
  cells: ReactNode[];
  title?: string;
}

/**
 * A ranked list: each row's label over a soft bar as long as its share of the
 * top row — one series, one colour, a wash rather than a block — with its figures
 * beside it in the text's own colours. The first eight, and all on asking.
 */
function BarList({ columns, rows, empty }: { columns: string[]; rows: BarRow[]; empty?: string }) {
  const { t } = useI18n();
  const [all, setAll] = useState(false);
  if (!rows.length) return <p className="py-8 text-center text-body-sm text-muted">{empty ?? t('analytics.none')}</p>;
  const max = Math.max(...rows.map((r) => r.value), 1);
  const shown = all ? rows : rows.slice(0, ROWS_SHOWN);
  return (
    <div>
      <div className="flex items-center gap-3 border-b hairline pb-1.5 text-overline uppercase text-[var(--text-tertiary)]">
        <span className="min-w-0 grow">{columns[0]}</span>
        {columns.slice(1).map((column) => (
          <span key={column} className="w-16 shrink-0 text-end">
            {column}
          </span>
        ))}
      </div>
      <ul>
        {shown.map((row) => (
          <li key={row.key} className="flex items-center gap-3 py-1" title={row.title}>
            <div className="relative min-w-0 grow">
              <span
                className="absolute inset-y-0 start-0 rounded-sm bg-primary/10 dark:bg-primary-400/15"
                style={{ width: `${Math.max(1, (row.value / max) * 100)}%` }}
                aria-hidden
              />
              <span className="relative block truncate px-2 py-1 text-body-sm text-[var(--text-primary)]">{row.label}</span>
            </div>
            {row.cells.map((cell, i) => (
              <span key={i} className="w-16 shrink-0 text-end text-body-sm tabular text-[var(--text-secondary)]">
                {cell}
              </span>
            ))}
          </li>
        ))}
      </ul>
      {rows.length > ROWS_SHOWN && (
        <button type="button" onClick={() => setAll(!all)} className="mt-2 text-body-sm font-medium text-primary-600 hover:underline dark:text-primary-400">
          {all ? t('analytics.showFewer') : t('analytics.showAll', { n: rows.length })}
        </button>
      )}
    </div>
  );
}

/** A page's address, opening the page itself in a new tab. */
function PageLink({ site, path }: { site: AnalyticsSite; path: string }) {
  const { t } = useI18n();
  const address = pageAddress(path);
  return (
    <a
      href={pageLink(site.address, path)}
      target="_blank"
      rel="noreferrer"
      dir="ltr"
      title={t('analytics.openPage', { page: address })}
      className="hover:underline"
    >
      {address}
    </a>
  );
}

/**
 * The pages, three ways: the most read (with the time spent on each), where
 * visits began (and how many left from there at once), and where they ended.
 */
function PagesCard({ report, site }: { report: TrafficReport; site: AnalyticsSite }) {
  const { t, locale } = useI18n();
  const n = (value: number) => formatNumber(value, locale);
  return (
    <SwitchCard
      title={t('analytics.pages')}
      views={[
        {
          key: 'top',
          label: t('analytics.pages.top'),
          body: (
            <BarList
              columns={[t('analytics.col.page'), t('analytics.col.visitors'), t('analytics.col.views'), t('analytics.col.time')]}
              rows={report.pages.map((p) => ({
                key: p.path,
                label: <PageLink site={site} path={p.path} />,
                value: p.visitors,
                cells: [n(p.visitors), n(p.views), p.seconds === null ? '—' : formatSpan(p.seconds, locale)],
              }))}
            />
          ),
        },
        {
          key: 'entry',
          label: t('analytics.pages.entry'),
          body: (
            <BarList
              columns={[t('analytics.col.page'), t('analytics.col.visits'), t('analytics.col.bounce')]}
              rows={report.entries.map((p) => ({
                key: p.path,
                label: <PageLink site={site} path={p.path} />,
                value: p.visits,
                cells: [n(p.visits), formatPercent(percentOf(p.bounces, p.visits), locale)],
              }))}
            />
          ),
        },
        {
          key: 'exit',
          label: t('analytics.pages.exit'),
          body: (
            <BarList
              columns={[t('analytics.col.page'), t('analytics.col.visits'), t('analytics.col.visitors')]}
              rows={report.exits.map((p) => ({ key: p.path, label: <PageLink site={site} path={p.path} />, value: p.visits, cells: [n(p.visits), n(p.visitors)] }))}
            />
          ),
        },
      ]}
      footer={t('analytics.timeHint')}
    />
  );
}

/** Who is on the site this minute, and on which page — read every 15 seconds with the badge above. */
function LiveCard({ live, site }: { live: LiveReport; site: AnalyticsSite }) {
  const { t, locale } = useI18n();
  return (
    <SwitchCard
      title={t('analytics.liveTitle')}
      subtitle={t('analytics.liveHint', { n: live.minutes })}
      views={[
        {
          key: 'now',
          label: t('analytics.liveTitle'),
          body: (
            <BarList
              columns={[t('analytics.col.page'), t('analytics.col.visitors')]}
              empty={
                live.lastVisit
                  ? t('analytics.liveEmptyLast', { when: relativeTime(live.lastVisit.at, locale), page: pageAddress(live.lastVisit.path) })
                  : t('analytics.noVisitsYet')
              }
              rows={live.pages.map((p) => ({
                key: p.path,
                label: <PageLink site={site} path={p.path} />,
                value: p.visitors,
                cells: [formatNumber(p.visitors, locale)],
              }))}
            />
          ),
        },
      ]}
    />
  );
}

/** A source's name: "Direct or unknown" for none; another site at the same address keeps its path. */
function sourceLabel(name: string | null, t: ReturnType<typeof useI18n>['t']): ReactNode {
  if (!name) return <span className="text-[var(--text-secondary)]">{t('analytics.direct')}</span>;
  return <bdi dir="ltr">{name}</bdi>;
}

function SourcesCard({ report }: { report: TrafficReport }) {
  const { t, locale } = useI18n();
  const n = (value: number) => formatNumber(value, locale);
  const totalVisits = report.totals.visits;
  return (
    <SwitchCard
      title={t('analytics.sources')}
      views={[
        {
          key: 'sources',
          label: t('analytics.sources.referrers'),
          body: (
            <BarList
              columns={[t('analytics.col.source'), t('analytics.col.visits'), t('analytics.col.share')]}
              rows={report.sources.map((s) => ({
                key: s.name ?? '',
                label: sourceLabel(s.name, t),
                value: s.visits,
                cells: [n(s.visits), formatPercent(percentOf(s.visits, totalVisits), locale)],
                title: s.name ? undefined : t('analytics.directHint'),
              }))}
            />
          ),
        },
        {
          key: 'campaigns',
          label: t('analytics.sources.campaigns'),
          body: (
            <BarList
              columns={[t('analytics.col.campaign'), t('analytics.col.visits'), t('analytics.col.visitors')]}
              empty={t('analytics.noCampaigns')}
              rows={report.campaigns.map((c) => ({
                key: `${c.source}|${c.medium}|${c.campaign}`,
                label: <bdi dir="ltr">{[c.source, c.medium, c.campaign].map((part) => part ?? '—').join(' / ')}</bdi>,
                value: c.visits,
                cells: [n(c.visits), n(c.visitors)],
              }))}
            />
          ),
        },
      ]}
      footer={t('analytics.sourcesHint')}
    />
  );
}

function ShareCard({
  title,
  subtitle,
  rows,
  name,
  footer,
}: {
  title: string;
  subtitle?: string;
  rows: TrafficReport['countries'];
  name: (value: string | null) => ReactNode;
  footer?: string;
}) {
  const { t, locale } = useI18n();
  const total = rows.reduce((sum, r) => sum + r.visitors, 0);
  return (
    <SwitchCard
      title={title}
      subtitle={subtitle}
      views={[
        {
          key: 'all',
          label: title,
          body: (
            <BarList
              columns={[title, t('analytics.col.visitors'), t('analytics.col.share')]}
              rows={rows.map((r) => ({
                key: r.name ?? '',
                label: name(r.name),
                value: r.visitors,
                cells: [formatNumber(r.visitors, locale), formatPercent(percentOf(r.visitors, total), locale)],
              }))}
            />
          ),
        },
      ]}
      footer={footer}
    />
  );
}

/** Names kept in English by the API for what has no brand. */
const AGENT_NAMES: Record<string, TranslationKey> = {
  'In-app browser': 'analytics.inApp',
  desktop: 'analytics.device.desktop',
  mobile: 'analytics.device.mobile',
  tablet: 'analytics.device.tablet',
};

function DevicesCard({ report }: { report: TrafficReport }) {
  const { t, locale } = useI18n();
  const view = (key: string, label: string, rows: TrafficReport['devices']) => {
    const total = rows.reduce((sum, r) => sum + r.visitors, 0);
    return {
      key,
      label,
      body: (
        <BarList
          columns={[label, t('analytics.col.visitors'), t('analytics.col.share')]}
          rows={rows.map((r) => ({
            key: r.name ?? '',
            label: r.name ? (AGENT_NAMES[r.name] ? t(AGENT_NAMES[r.name]) : r.name) : t('analytics.unknown'),
            value: r.visitors,
            cells: [formatNumber(r.visitors, locale), formatPercent(percentOf(r.visitors, total), locale)],
          }))}
        />
      ),
    };
  };
  return (
    <SwitchCard
      title={t('analytics.devices')}
      views={[
        view('devices', t('analytics.devices.devices'), report.devices),
        view('browsers', t('analytics.devices.browsers'), report.browsers),
        view('systems', t('analytics.devices.systems'), report.systems),
      ]}
    />
  );
}

/**
 * Page views by weekday and hour, the company's week from its first day: one hue,
 * deeper where busier, in five steps with their key — and every cell's figure on
 * hover, for the reader the colour does not reach.
 */
function HoursGrid({ report }: { report: TrafficReport }) {
  const { t, locale } = useI18n();
  const byCell = useMemo(() => new Map(report.hours.map((h) => [`${h.day}:${h.hour}`, h.views])), [report.hours]);
  const max = Math.max(0, ...report.hours.map((h) => h.views));
  if (max === 0) return <p className="py-8 text-center text-body-sm text-muted">{t('analytics.chartEmpty')}</p>;
  const days = Array.from({ length: 7 }, (_, i) => (report.range.weekStart + i) % 7);
  const step = (views: number) => (views === 0 ? 0 : Math.min(4, Math.ceil((views / max) * 4)));
  const shade = (level: number) =>
    level === 0 ? undefined : { background: `color-mix(in srgb, ${VISITORS} ${[0, 22, 45, 70, 100][level]}%, transparent)` };

  return (
    <div dir="ltr" className="overflow-x-auto">
      <div className="grid min-w-[36rem] gap-[2px]" style={{ gridTemplateColumns: 'minmax(3.5rem, auto) repeat(24, minmax(0, 1fr))' }}>
        <span />
        {Array.from({ length: 24 }, (_, hour) => (
          <span key={hour} className="text-center text-[10px] tabular text-[var(--text-tertiary)]">
            {hour % 3 === 0 ? String(hour).padStart(2, '0') : ''}
          </span>
        ))}
        {days.map((day) => (
          <HoursRow key={day} day={day} byCell={byCell} step={step} shade={shade} name={DAY_NAMES[locale][day]} />
        ))}
      </div>
      <div className="mt-3 flex items-center justify-end gap-1.5 text-caption text-[var(--text-secondary)]" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
        {t('analytics.fewer')}
        {[0, 1, 2, 3, 4].map((level) => (
          <span key={level} className="inline-block h-3 w-3 rounded-xs bg-[var(--surface-sunken)]" style={shade(level)} aria-hidden />
        ))}
        {t('analytics.more')}
      </div>
    </div>
  );
}

function HoursRow({
  day,
  name,
  byCell,
  step,
  shade,
}: {
  day: number;
  name: string;
  byCell: Map<string, number>;
  step: (views: number) => number;
  shade: (level: number) => CSSProperties | undefined;
}) {
  const { t, locale } = useI18n();
  return (
    <>
      <span className="truncate pe-2 text-caption text-[var(--text-secondary)]" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
        {name}
      </span>
      {Array.from({ length: 24 }, (_, hour) => {
        const views = byCell.get(`${day}:${hour}`) ?? 0;
        const label = t('analytics.cell', { day: name, hour: String(hour).padStart(2, '0'), n: formatNumber(views, locale) });
        return (
          <span
            key={hour}
            role="img"
            aria-label={label}
            title={label}
            className="h-5 rounded-xs bg-[var(--surface-sunken)] transition-opacity hover:opacity-70"
            style={shade(step(views))}
          />
        );
      })}
    </>
  );
}

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

const BOT_NAMES: Record<string, TranslationKey> = {
  'No browser named': 'analytics.botName.noBrowser',
  'Other crawler': 'analytics.botName.other',
  'Headless browser': 'analytics.botName.headless',
  Script: 'analytics.botName.script',
  'Link preview service': 'analytics.botName.previewService',
};

const BOT_TONES: Record<BotKind, 'info' | 'plasma' | 'neutral' | 'success' | 'warning'> = {
  search: 'info',
  preview: 'success',
  ai: 'plasma',
  seo: 'neutral',
  monitor: 'neutral',
  script: 'warning',
  other: 'neutral',
};

function RequestsTab({ id, range }: { id: string; range: RangeKey }) {
  const { t, locale } = useI18n();
  const report = useQuery({
    queryKey: [...SITES_KEY, 'analytics', id, 'requests', range],
    queryFn: () => api.get<RequestsReport>(`/sites/${id}/analytics/requests`, { range }),
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });
  const data = report.data;
  if (report.isLoading) return <LoadingBlock />;
  if (report.error || !data) return <ErrorState error={report.error ?? new Error(t('common.error'))} onRetry={() => void report.refetch()} />;

  const n = (value: number) => formatNumber(value, locale);
  const requests = data.pages.requests + data.files.requests;
  const notModified = data.pages.notModified + data.files.notModified;
  const ms = (value: number | null) => (value === null ? '—' : t('analytics.ms', { n: formatNumber(Math.round(value), locale) }));

  return (
    <div className={clsx('space-y-4 transition-opacity', report.isPlaceholderData && 'opacity-60')} aria-busy={report.isFetching}>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <Metric label={t('analytics.req.requests')} labelLines={2} value={n(requests)} hint={t('analytics.req.requestsHint')} />
        <Metric label={t('analytics.req.data')} labelLines={2} value={formatBytes(data.pages.bytes + data.files.bytes, locale)} />
        <Metric
          label={t('analytics.req.cached')}
          labelLines={2}
          value={requests ? formatPercent(percentOf(notModified, requests), locale) : '—'}
          hint={t('analytics.req.cachedHint')}
        />
        <Metric
          label={t('analytics.req.speed')}
          labelLines={2}
          value={ms(data.speed.median)}
          hint={data.speed.p95 === null ? undefined : t('analytics.req.speedHint', { ms: ms(data.speed.p95) })}
        />
        <Metric
          label={t('analytics.req.notFound')}
          labelLines={2}
          value={n(data.notFound.reduce((sum, row) => sum + row.requests, 0))}
          hint={t('analytics.req.notFoundHint')}
          tone={data.notFound.length ? 'warning' : 'neutral'}
        />
        <Metric label={t('analytics.req.robots')} labelLines={2} value={n(data.pages.bots)} hint={t('analytics.req.robotsHint', { n: n(data.files.bots) })} />
      </div>

      <Card>
        <CardHeader
          title={t('analytics.req.daily')}
          subtitle={t('analytics.req.fileDays', { from: dayLabel(data.fileDays.from, locale), to: dayLabel(data.fileDays.to, locale) })}
        />
        <DailyBars rows={data.daily} />
      </Card>

      <Card padded={false}>
        <div className="px-5 pt-5">
          <CardHeader title={t('analytics.req.notFoundTitle')} subtitle={t('analytics.req.notFoundNote')} />
        </div>
        {data.notFound.length === 0 ? (
          <p className="px-5 pb-8 pt-2 text-center text-body-sm text-muted">{t('analytics.req.notFoundEmpty')}</p>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>{t('analytics.col.path')}</Th>
                <Th className="hidden sm:table-cell">{t('analytics.col.kind')}</Th>
                <Th align="end">{t('analytics.col.times')}</Th>
                <Th align="end" className="hidden md:table-cell">
                  {t('analytics.col.lastSeen')}
                </Th>
              </tr>
            </thead>
            <tbody>
              {data.notFound.map((row) => (
                <Tr key={`${row.page}:${row.path}`}>
                  <Td className="max-w-[24rem]">
                    <bdi dir="ltr" className="block truncate font-mono text-caption">
                      /{row.path}
                    </bdi>
                  </Td>
                  <Td className="hidden sm:table-cell">
                    <Badge tone={row.page ? 'warning' : 'neutral'}>{t(row.page ? 'analytics.req.kindPage' : 'analytics.req.kindFile')}</Badge>
                  </Td>
                  <Td align="end" className="tabular">
                    {n(row.requests)}
                  </Td>
                  <Td align="end" className="hidden whitespace-nowrap text-muted md:table-cell">
                    {row.lastAt ? relativeTime(row.lastAt, locale) : dayLabel(row.lastDay, locale)}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <div className="grid gap-4 xl:grid-cols-2">
        <Card padded={false} className="min-w-0">
          <div className="px-5 pt-5">
            <CardHeader title={t('analytics.req.robotsTitle')} subtitle={t('analytics.req.robotsNote')} />
          </div>
          {data.bots.length === 0 ? (
            <p className="px-5 pb-8 pt-2 text-center text-body-sm text-muted">{t('analytics.req.robotsEmpty')}</p>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>{t('analytics.col.robot')}</Th>
                  <Th>{t('analytics.col.kind')}</Th>
                  <Th align="end">{t('analytics.col.views')}</Th>
                  <Th align="end" className="hidden sm:table-cell">
                    {t('analytics.col.lastSeen')}
                  </Th>
                </tr>
              </thead>
              <tbody>
                {data.bots.map((bot) => (
                  <Tr key={bot.name}>
                    <Td>{BOT_NAMES[bot.name] ? t(BOT_NAMES[bot.name]) : bot.name}</Td>
                    <Td>
                      <Badge tone={BOT_TONES[bot.kind]}>{t(`analytics.bot.${bot.kind}` as TranslationKey)}</Badge>
                    </Td>
                    <Td align="end" className="tabular" >
                      <span title={t('analytics.req.botPages', { n: n(bot.pages) })}>{n(bot.views)}</span>
                    </Td>
                    <Td align="end" className="hidden whitespace-nowrap text-muted sm:table-cell">
                      {relativeTime(bot.last, locale)}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>

        <Card padded={false} className="min-w-0">
          <div className="px-5 pt-5">
            <CardHeader title={t('analytics.req.filesTitle')} subtitle={t('analytics.req.filesNote')} />
          </div>
          {data.topFiles.length === 0 ? (
            <p className="px-5 pb-8 pt-2 text-center text-body-sm text-muted">{t('analytics.req.filesEmpty')}</p>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>{t('analytics.col.file')}</Th>
                  <Th align="end">{t('analytics.col.requests')}</Th>
                  <Th align="end" className="hidden sm:table-cell">
                    {t('analytics.col.data')}
                  </Th>
                  <Th align="end" className="hidden md:table-cell">
                    {t('analytics.col.cached')}
                  </Th>
                </tr>
              </thead>
              <tbody>
                {data.topFiles.slice(0, 25).map((file) => (
                  <Tr key={file.path}>
                    <Td className="max-w-[18rem]">
                      <bdi dir="ltr" className="block truncate font-mono text-caption">
                        {file.path}
                      </bdi>
                    </Td>
                    <Td align="end" className="tabular">
                      {n(file.requests)}
                    </Td>
                    <Td align="end" className="hidden whitespace-nowrap tabular text-muted sm:table-cell">
                      {formatBytes(file.bytes, locale)}
                    </Td>
                    <Td align="end" className="hidden tabular text-muted md:table-cell">
                      {formatPercent(percentOf(file.cached, file.requests), locale)}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      </div>
    </div>
  );
}

/** What went out day by day: one series, a column a day. */
function DailyBars({ rows }: { rows: RequestsReport['daily'] }) {
  const { t, locale } = useI18n();
  if (!rows.some((r) => r.requests > 0)) return <p className="py-12 text-center text-body-sm text-muted">{t('analytics.req.nothingSent')}</p>;
  return (
    <div className="h-56" dir="ltr">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
          <CartesianGrid stroke="var(--line)" vertical={false} />
          <XAxis
            dataKey="day"
            tickFormatter={(value: string) => bucketLabel(value, 'day', locale)}
            tick={{ fill: 'var(--text-tertiary)', fontSize: 11 }}
            axisLine={{ stroke: 'var(--line)' }}
            tickLine={false}
            minTickGap={18}
          />
          <YAxis
            width={64}
            tickFormatter={(value: number) => formatBytes(value, locale)}
            tick={{ fill: 'var(--text-tertiary)', fontSize: 11 }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            cursor={{ fill: 'var(--surface-sunken)' }}
            content={({ active, payload }) => {
              const row = active ? (payload?.[0]?.payload as RequestsReport['daily'][number] | undefined) : undefined;
              if (!row) return null;
              return (
                <ChartTip title={bucketLabel(row.day, 'day', locale, true)}>
                  <TipLine color={VIEWS} value={formatBytes(row.bytes, locale)} label={t('analytics.req.data')} />
                  <p className="text-caption text-muted">{t('analytics.req.dailyRequests', { n: formatNumber(row.requests, locale) })}</p>
                </ChartTip>
              );
            }}
          />
          <Bar dataKey="bytes" fill={VIEWS} maxBarSize={24} radius={[4, 4, 0, 0]} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Site check
// ---------------------------------------------------------------------------

const LEVEL_ICON: Record<CheckLevel, ReactNode> = {
  error: <XCircle className="h-5 w-5 shrink-0 text-error-fg dark:text-error-dark" aria-hidden />,
  warning: <AlertTriangle className="h-5 w-5 shrink-0 text-warning-fg dark:text-warning-dark" aria-hidden />,
  info: <Info className="h-5 w-5 shrink-0 text-info-fg dark:text-info-dark" aria-hidden />,
  pass: <CheckCircle2 className="h-5 w-5 shrink-0 text-success-fg dark:text-success" aria-hidden />,
};

const LEVEL_ORDER: Record<CheckLevel, number> = { error: 0, warning: 1, info: 2, pass: 3 };

const CHANGE_FIELDS: Record<string, TranslationKey> = {
  name: 'sites.name',
  folder: 'sites.folder',
  description: 'sites.description',
  isLive: 'analytics.field.isLive',
  searchable: 'analytics.field.searchable',
};

function SiteTab({ id, site }: { id: string; site: AnalyticsSite }) {
  const { t, locale, localized } = useI18n();
  const report = useQuery({
    queryKey: [...SITES_KEY, 'analytics', id, 'site'],
    queryFn: () => api.get<SiteReport>(`/sites/${id}/analytics/site`),
  });
  const data = report.data;
  if (report.isLoading) return <LoadingBlock label={t('analytics.check.reading')} />;
  if (report.error || !data) return <ErrorState error={report.error ?? new Error(t('common.error'))} onRetry={() => void report.refetch()} />;

  const count = (level: CheckLevel) => data.checks.filter((c) => c.level === level).length;
  const checks = [...data.checks].sort((a, b) => LEVEL_ORDER[a.level] - LEVEL_ORDER[b.level]);
  const n = (value: number) => formatNumber(value, locale);

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Metric label={t('analytics.check.errors')} value={n(count('error'))} tone={count('error') ? 'error' : 'neutral'} icon={<XCircle className="h-4 w-4" />} />
        <Metric
          label={t('analytics.check.warnings')}
          value={n(count('warning'))}
          tone={count('warning') ? 'warning' : 'neutral'}
          icon={<AlertTriangle className="h-4 w-4" />}
        />
        <Metric label={t('analytics.check.suggestions')} value={n(count('info'))} icon={<Info className="h-4 w-4" />} />
        <Metric label={t('analytics.check.passed')} value={n(count('pass'))} tone="success" icon={<CheckCircle2 className="h-4 w-4" />} />
      </div>

      <Card padded={false}>
        <div className="flex flex-wrap items-start justify-between gap-2 px-5 pt-5">
          <CardHeader
            className="mb-2"
            title={t('analytics.check.title')}
            subtitle={t('analytics.check.scanned', {
              pages: n(data.scanned.pages),
              styles: n(data.scanned.styles),
              when: relativeTime(data.scanned.at, locale),
            })}
          />
          <Button size="sm" loading={report.isFetching} onClick={() => void report.refetch()}>
            {t('analytics.check.again')}
          </Button>
        </div>
        {data.scanned.skipped > 0 && <p className="px-5 pb-2 text-caption text-faint">{t('analytics.check.skipped', { n: n(data.scanned.skipped) })}</p>}
        <ul className="divide-y divide-[var(--line)] border-t hairline">
          {checks.map((check) => (
            <CheckRow key={check.id} check={check} />
          ))}
        </ul>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="min-w-0">
          <CardHeader
            title={t('analytics.content.title')}
            subtitle={t('analytics.content.summary', {
              files: n(data.content.files),
              size: formatBytes(data.content.bytes, locale),
              folders: n(data.content.folders),
            })}
          />
          <BarList
            columns={[t('analytics.col.kind'), t('sites.files'), t('analytics.col.data')]}
            rows={data.content.kinds.map((k) => ({
              key: k.kind,
              label: t(`analytics.kind.${k.kind}` as TranslationKey),
              value: k.bytes,
              cells: [n(k.files), formatBytes(k.bytes, locale)],
            }))}
          />
          {data.content.createdAt && (
            <p className="mt-3 text-caption text-faint">
              {t('analytics.content.created', {
                when: formatDateTime(data.content.createdAt, locale),
                name: data.content.createdBy ? localized(data.content.createdBy) : '—',
              })}
            </p>
          )}
        </Card>

        <Card className="min-w-0">
          <CardHeader title={t('analytics.content.largest')} />
          <BarList
            columns={[t('analytics.col.file'), t('sites.size')]}
            rows={data.content.largest.map((f) => ({ key: f.path, label: <bdi dir="ltr">{f.path}</bdi>, value: f.size, cells: [formatBytes(f.size, locale)] }))}
          />
        </Card>

        <Card padded={false} className="min-w-0">
          <div className="px-5 pt-5">
            <CardHeader title={t('analytics.content.pagesTitle', { n: n(data.pages.length) })} />
          </div>
          {data.pages.length === 0 ? (
            <EmptyState title={t('analytics.content.noPages')} hint="" />
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>{t('analytics.col.page')}</Th>
                  <Th>{t('analytics.content.pageTitle')}</Th>
                  <Th align="end" className="hidden sm:table-cell">
                    {t('sites.size')}
                  </Th>
                </tr>
              </thead>
              <tbody>
                {data.pages.slice(0, 50).map((page) => (
                  <Tr key={page.path}>
                    <Td className="max-w-[12rem]">
                      <a href={pageLink(site.address, page.path)} target="_blank" rel="noreferrer" dir="ltr" className="block truncate hover:underline">
                        {pageAddress(page.path)}
                      </a>
                    </Td>
                    <Td className="max-w-[16rem]">
                      {page.title ? (
                        <span className="block truncate">
                          <bdi>{page.title}</bdi>
                        </span>
                      ) : (
                        <span className="text-faint">{t('analytics.content.noTitle')}</span>
                      )}
                    </Td>
                    <Td align="end" className="hidden whitespace-nowrap tabular text-muted sm:table-cell">
                      {formatBytes(page.size, locale)}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>

        <Card className="min-w-0">
          <CardHeader title={t('analytics.content.thirdParty')} subtitle={t('analytics.content.thirdPartyHint')} />
          <BarList
            columns={[t('analytics.col.address'), t('analytics.col.pages')]}
            empty={t('analytics.content.thirdPartyNone')}
            rows={data.thirdParty.map((host) => ({
              key: host.host,
              label: (
                <span className="inline-flex items-center gap-2">
                  <bdi dir="ltr">{host.host}</bdi>
                  <span className="text-caption text-faint">{host.kinds.map((kind) => t(`analytics.ref.${kind}` as TranslationKey)).join(' · ')}</span>
                  {host.insecure && <Badge tone="warning">{t('analytics.content.http')}</Badge>}
                </span>
              ),
              value: host.pages,
              cells: [n(host.pages)],
            }))}
          />
          <h4 className="mb-2 mt-5 text-body-sm font-semibold text-[var(--text-primary)]">{t('analytics.content.outbound')}</h4>
          <BarList
            columns={[t('analytics.col.address'), t('analytics.col.links')]}
            empty={t('analytics.content.outboundNone')}
            rows={data.outbound.map((host) => ({ key: host.host, label: <bdi dir="ltr">{host.host}</bdi>, value: host.links, cells: [n(host.links)] }))}
          />
        </Card>
      </div>

      <Card>
        <CardHeader title={t('analytics.changes.title')} subtitle={t('analytics.changes.subtitle')} />
        {data.changes.length === 0 ? (
          <p className="py-6 text-center text-body-sm text-muted">{t('analytics.changes.none')}</p>
        ) : (
          <ol className="space-y-2">
            {data.changes.map((change) => (
              <li key={change.id} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-body-sm">
                <span className="w-32 shrink-0 whitespace-nowrap text-caption text-muted" title={formatDateTime(change.at, locale)}>
                  {relativeTime(change.at, locale)}
                </span>
                <span className="font-medium text-[var(--text-primary)]">{changeLabel(change, t)}</span>
                {change.path && (
                  <bdi dir="ltr" className="font-mono text-caption text-[var(--text-secondary)]">
                    {change.from ? `${change.from} → ${change.path}` : change.path}
                  </bdi>
                )}
                {change.action === 'site.updated' && change.fields && change.fields.length > 0 && (
                  <span className="text-[var(--text-secondary)]">
                    {change.fields.map((field) => (CHANGE_FIELDS[field] ? t(CHANGE_FIELDS[field]) : field)).join(' · ')}
                  </span>
                )}
                <span className="text-faint">— {change.by ? localized(change.by) : t('analytics.changes.system')}</span>
              </li>
            ))}
          </ol>
        )}
      </Card>
    </div>
  );
}

function changeLabel(change: SiteReport['changes'][number], t: ReturnType<typeof useI18n>['t']): string {
  switch (change.action) {
    case 'site.created':
      return t('analytics.change.created');
    case 'site.updated':
      return t('analytics.change.updated');
    case 'site.file_added':
      return t('analytics.change.added');
    case 'site.file_replaced':
      return t('analytics.change.replaced');
    case 'site.file_moved':
      return t('analytics.change.moved');
    case 'site.files_deleted':
      return t('analytics.change.deleted', { n: change.files ?? 0 });
    default:
      return change.action;
  }
}

/** One check: its verdict, what it means, and — opened — where on the site. */
function CheckRow({ check }: { check: SiteReport['checks'][number] }) {
  const { t, locale } = useI18n();
  const passed = check.level === 'pass';
  const text = t(`analytics.check.${check.id}.${passed ? 'pass' : 'fail'}` as TranslationKey, { n: formatNumber(check.count, locale) });
  const more = check.count - check.items.length;
  return (
    <li className="px-5 py-3">
      <details className="group" open={!passed && check.level === 'error'}>
        <summary className={clsx('flex list-none items-start gap-3', !passed && 'cursor-pointer')}>
          {LEVEL_ICON[check.level]}
          <span className="min-w-0 grow">
            <span className="block font-medium text-[var(--text-primary)]">{t(`analytics.check.${check.id}.title` as TranslationKey)}</span>
            <span className="block text-body-sm text-[var(--text-secondary)]">{text}</span>
          </span>
          {!passed && check.items.length > 0 && (
            <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-[var(--text-tertiary)] transition-transform flip-rtl group-open:rotate-90" aria-hidden />
          )}
        </summary>
        {!passed && check.items.length > 0 && (
          <ul className="ms-8 mt-2 space-y-1 text-caption">
            {check.items.map((item, i) => (
              <li key={i} className="flex flex-wrap gap-x-2 text-[var(--text-secondary)]" dir="ltr">
                {item.page && <span className="font-mono text-[var(--text-primary)]">{item.page}</span>}
                {item.page && item.target !== undefined && <span className="text-faint">→</span>}
                {item.target !== undefined && <span className="break-all font-mono">{item.target || '—'}</span>}
                {item.path && <span className="font-mono text-[var(--text-primary)]">{item.path}</span>}
                {item.size !== undefined && <span className="text-faint">{formatBytes(item.size, locale)}</span>}
                {item.title && <span className="text-[var(--text-primary)]">“{item.title}”</span>}
                {item.pages && <span className="font-mono">{item.pages.join(', ')}</span>}
              </li>
            ))}
            {more > 0 && <li className="text-faint">{t('analytics.check.more', { n: formatNumber(more, locale) })}</li>}
          </ul>
        )}
      </details>
    </li>
  );
}
